/**
 * CalibrationTool Component
 * Throughput calibration tool for PE320 extruder
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CalibrationTool.css';
import { downloadToolpathZip } from './toolpath-export';
import { RelatedGuide } from '../../components/ui';
import { chartColours, useTheme } from '../../utils/theme';

const NOZZLE_SIZES = [3, 5, 8, 10, 12];
const SCREW_RPM_MIN = 5;
const CALIB_POINTS = 10;

const maxScrewRpmForNozzle = (nozzle) => {
  const n = parseFloat(nozzle);
  if (n === 3) return 80;
  if (n === 5) return 120;
  if (n === 8 || n === 10 || n === 12) return 160;
  return null;
};

const clampLimit = (value, floor, ceiling, fallback) => {
  const n = parseFloat(value);
  if (isNaN(n)) return String(fallback);
  return String(Math.round(Math.min(ceiling, Math.max(floor, n))));
};

const clampTableRpm = (value, floor, ceiling) => {
  if (value === '' || value == null) return '';
  const n = parseFloat(value);
  if (isNaN(n)) return '';
  if (n <= 0) return '0';
  return String(Math.round(Math.min(ceiling, Math.max(floor, n))));
};

const collectTableRpms = (rows) => (
  rows
    .map((r) => parseFloat(r.rpm))
    .filter((v) => !isNaN(v))
);

const CalibrationTool = () => {
  const [materialName, setMaterialName] = useState('');
  const [nozzleSize, setNozzleSize] = useState('');
  const [minRpm, setMinRpm] = useState('');
  const [maxRpm, setMaxRpm] = useState('');
  const [rows, setRows] = useState([
    { id: 1, gmin: '', rpm: '' },
    { id: 2, gmin: '', rpm: '' },
    { id: 3, gmin: '', rpm: '' }
  ]);
  const [coefficients, setCoefficients] = useState(null);
  const [fit, setFit] = useState(null);
  const [libsReady, setLibsReady] = useState(false);
  const [csvExported, setCsvExported] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', error: false });

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const nextRowId = useRef(4);
  const nozzleMax = maxScrewRpmForNozzle(nozzleSize);

  const markDirty = () => setCsvExported(false);

  const loadExternalScript = (src) => new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true' || existing.getAttribute('data-loaded') === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  useEffect(() => {
    let cancelled = false;
    const MATH_SRC = 'https://unpkg.com/mathjs@12.4.1/lib/browser/math.js';
    const CHART_SRC = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';

    const ensureLibs = async () => {
      const jobs = [];
      if (!window.math) jobs.push(loadExternalScript(MATH_SRC));
      if (!window.Chart) jobs.push(loadExternalScript(CHART_SRC));
      try {
        await Promise.all(jobs);
      } catch (error) {
        console.error('Failed to load calibration libraries', error);
        return;
      }
      if (!cancelled && window.math && window.Chart) {
        setLibsReady(true);
      }
    };

    if (window.math && window.Chart) {
      setLibsReady(true);
    } else {
      ensureLibs();
    }

    return () => {
      cancelled = true;
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  const notify = (message, error = false) => {
    setNotification({ show: true, message, error });
    setTimeout(() => setNotification({ show: false, message: '', error: false }), 3000);
  };

  const applyNozzleClamp = (size, nextMin, nextMax, nextRows, resetLimits = false) => {
    const ceiling = maxScrewRpmForNozzle(size);
    if (!ceiling) {
      setNozzleSize(size);
      return;
    }

    const clampedMin = resetLimits
      ? String(SCREW_RPM_MIN)
      : clampLimit(nextMin, SCREW_RPM_MIN, ceiling - 1, SCREW_RPM_MIN);
    let clampedMax = resetLimits
      ? String(ceiling)
      : clampLimit(nextMax, SCREW_RPM_MIN + 1, ceiling, ceiling);
    if (parseFloat(clampedMin) >= parseFloat(clampedMax)) {
      clampedMax = String(ceiling);
    }

    setNozzleSize(size);
    setMinRpm(clampedMin);
    setMaxRpm(clampedMax);
    setRows(nextRows.map((row) => ({
      ...row,
      rpm: clampTableRpm(row.rpm, SCREW_RPM_MIN, ceiling)
    })));
  };

  const calculateKgHr = (gmin) => {
    const g = parseFloat(gmin);
    return isNaN(g) ? '—' : ((g * 60) / 1000).toFixed(4);
  };

  const handleRowChange = (id, field, value) => {
    markDirty();
    setRows(rows.map((row) =>
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const handleRowRpmBlur = (id, value) => {
    if (!nozzleMax) return;
    setRows((prev) => prev.map((row) =>
      row.id === id ? { ...row, rpm: clampTableRpm(value, SCREW_RPM_MIN, nozzleMax) } : row
    ));
  };

  const addRow = (gmin = '', rpm = '') => {
    markDirty();
    setRows([...rows, { id: nextRowId.current++, gmin, rpm }]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) {
      markDirty();
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleNozzleChange = (size) => {
    applyNozzleClamp(size, minRpm, maxRpm, rows, true);
  };

  const handleMinRpmBlur = () => {
    if (!nozzleMax) return;
    applyNozzleClamp(nozzleSize, minRpm, maxRpm, rows);
  };

  const handleMaxRpmBlur = () => {
    if (!nozzleMax) return;
    applyNozzleClamp(nozzleSize, minRpm, maxRpm, rows);
  };

  const autoFillRpm = () => {
    const ceiling = maxScrewRpmForNozzle(nozzleSize);
    if (!ceiling) {
      notify('Select a nozzle size first', true);
      return;
    }

    const max = parseFloat(clampLimit(maxRpm, SCREW_RPM_MIN + 1, ceiling, ceiling));
    const min = parseFloat(clampLimit(minRpm, SCREW_RPM_MIN, max - 1, SCREW_RPM_MIN));

    if (min >= max) {
      notify('Min screw RPM must be less than Max screw RPM', true);
      return;
    }

    setMinRpm(String(min));
    setMaxRpm(String(max));

    const step = (max - min) / (CALIB_POINTS - 1);
    const newRows = [];
    for (let i = 0; i < CALIB_POINTS; i++) {
      const rpm = Math.round(max - i * step);
      newRows.push({ id: nextRowId.current++, gmin: '', rpm: String(rpm) });
    }

    setRows(newRows);
    markDirty();
    notify(`Screw RPM auto-filled (${CALIB_POINTS} points from ${max} to ${min})`);
  };

  const drawChart = useCallback((xs, ys, beta) => {
    if (!window.Chart || !chartRef.current) return;

    const min = Math.min(...xs);
    const max = Math.max(...xs);
    const pts = 100;
    const step = (max - min) / pts;
    const curveX = [];
    const curveY = [];

    for (let i = 0; i <= pts; i++) {
      const x = min + i * step;
      curveX.push(x);
      curveY.push(beta[0] * Math.pow(x, 3) + beta[1] * Math.pow(x, 2) + beta[2] * x + beta[3]);
    }

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const chart = chartColours();
    chartInstance.current = new window.Chart(chartRef.current, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: 'Measured',
            data: xs.map((x, i) => ({ x: xs[i], y: ys[i] })),
            pointRadius: 6,
            backgroundColor: '#6e8196' // steel (Graphite & Steel)
          },
          {
            label: 'Fit',
            data: curveX.map((x, i) => ({ x, y: curveY[i] })),
            type: 'line',
            borderWidth: 2,
            pointRadius: 0,
            borderColor: '#4CAF50'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: { display: true, text: 'Throughput (kg/hr)', color: chart.ink },
            ticks: { color: chart.ink },
            grid: { color: chart.grid }
          },
          y: {
            title: { display: true, text: 'Screw Speed (rpm)', color: chart.ink },
            ticks: { color: chart.ink },
            grid: { color: chart.grid }
          }
        },
        plugins: {
          legend: { labels: { color: chart.ink } }
        }
      }
    });
  }, []);

  // Redraw the canvas chart in the new colours when the theme changes
  const theme = useTheme();
  useEffect(() => {
    if (!fit || !libsReady || !coefficients) return;
    drawChart(fit.xs, fit.ys, fit.beta);
  }, [fit, libsReady, coefficients, drawChart, theme]);

  useEffect(() => {
    const hasRowData = rows.some((row) => row.gmin !== '' || row.rpm !== '');
    const dirty = (hasRowData || Boolean(coefficients)) && !csvExported;
    const onBeforeUnload = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [rows, coefficients, csvExported]);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`${label} copied`);
    } catch (error) {
      notify('Copy failed', true);
    }
  };

  const copyAllCoefficients = () => {
    if (!coefficients) return;
    const lines = [
      `Throughput calibration - linear (x): ${coefficients.coef1}`,
      `Throughput calibration - quadratic (x2): ${coefficients.coef2}`,
      `Throughput calibration - cubic (x3): ${coefficients.coef3}`,
      `Throughput calibration - intercept (y): ${coefficients.coef0}`
    ];
    copyText(lines.join('\n'), 'Coefficients');
  };

  const calculateCoefficients = () => {
    if (!window.math) {
      notify('Math library not loaded. Please refresh the page.', true);
      return;
    }

    const xs = [];
    const ys = [];

    rows.forEach((row) => {
      const g = parseFloat(row.gmin);
      const rpm = parseFloat(row.rpm);
      if (!isNaN(g) && !isNaN(rpm)) {
        xs.push((g * 60) / 1000);
        ys.push(rpm);
      }
    });

    if (xs.length < 4) {
      notify('Need at least 4 valid rows', true);
      return;
    }

    const X = xs.map((x) => [Math.pow(x, 3), Math.pow(x, 2), x, 1]);
    const beta = window.math.multiply(
      window.math.inv(window.math.multiply(window.math.transpose(X), X)),
      window.math.multiply(window.math.transpose(X), ys)
    );

    setCoefficients({
      coef3: beta[0].toPrecision(10),
      coef2: beta[1].toPrecision(10),
      coef1: beta[2].toPrecision(10),
      coef0: beta[3].toPrecision(10)
    });
    setFit({ xs, ys, beta });
    notify('Coefficients calculated');
  };

  const exportCsv = () => {
    if (!coefficients) {
      notify('Please calculate coefficients first', true);
      return;
    }

    let csv = `Material: ${materialName || 'Unknown'}\nNozzle Size: ${nozzleSize || 'Unknown'} mm\n\n`;
    csv += 'g/min,kg/hr,screw_rpm\n';

    rows.forEach((row) => {
      if (row.gmin && row.rpm) {
        csv += `${row.gmin},${calculateKgHr(row.gmin)},${row.rpm}\n`;
      }
    });

    csv += '\nCoefficients\nlinear_x,quadratic_x2,cubic_x3,intercept_y\n';
    csv += `${coefficients.coef1},${coefficients.coef2},${coefficients.coef3},${coefficients.coef0}`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'throughput_calibration.csv';
    link.click();
    setCsvExported(true);
    notify('CSV exported');
  };

  const exportSrc = () => {
    const ceiling = maxScrewRpmForNozzle(nozzleSize);
    if (!ceiling) {
      notify('Select a nozzle size first', true);
      return;
    }

    const rpms = collectTableRpms(rows);
    if (rpms.length === 0) {
      notify('No valid screw RPM values to export', true);
      return;
    }

    const outOfRange = rpms.find((v) => v !== 0 && (v < SCREW_RPM_MIN || v > ceiling));
    if (outOfRange !== undefined) {
      notify(`Screw RPM must be between ${SCREW_RPM_MIN} and ${ceiling} for this nozzle`, true);
      return;
    }

    downloadToolpathZip(rpms, {
      materialName,
      nozzleSize,
      minRpm,
      maxRpm,
      date: new Date().toLocaleDateString()
    });
    notify('Toolpath.zip exported (src + dat)');
  };

  const saveData = () => {
    const data = {
      materialName,
      nozzleSize,
      minRpm,
      maxRpm,
      date: new Date().toLocaleDateString(),
      rows: rows.filter((r) => r.gmin || r.rpm).map((r) => ({ g: r.gmin, rpm: r.rpm }))
    };

    let calibrations = [];
    const savedData = localStorage.getItem('throughputCalibrations');
    if (savedData) {
      try {
        calibrations = JSON.parse(savedData);
      } catch (error) {
        console.error('Error parsing saved data:', error);
      }
    }

    calibrations.push(data);
    localStorage.setItem('throughputCalibrations', JSON.stringify(calibrations));
    notify('Data saved successfully');
  };

  const loadData = () => {
    const savedData = localStorage.getItem('throughputCalibrations');
    if (!savedData) {
      notify('No saved calibrations found', true);
      return;
    }

    try {
      const calibrations = JSON.parse(savedData);
      if (calibrations.length === 0) {
        notify('No saved calibrations found', true);
        return;
      }

      const latest = calibrations[calibrations.length - 1];
      setMaterialName(latest.materialName || '');

      const loadedNozzle = String(latest.nozzleSize || '');
      const loadedRows = (latest.rows && latest.rows.length > 0)
        ? latest.rows.map((row) => ({
            id: nextRowId.current++,
            gmin: row.g || '',
            rpm: row.rpm || ''
          }))
        : rows;

      if (maxScrewRpmForNozzle(loadedNozzle)) {
        applyNozzleClamp(loadedNozzle, latest.minRpm, latest.maxRpm, loadedRows);
      } else {
        setNozzleSize('');
        setMinRpm(latest.minRpm || '');
        setMaxRpm(latest.maxRpm || '');
        setRows(loadedRows);
      }

      markDirty();
      notify('Calibration loaded successfully');
    } catch (error) {
      notify('Error loading calibration', true);
      console.error('Error loading data:', error);
    }
  };

  return (
    <div className="calibration-tool">
      <div className={`notification ${notification.show ? 'show' : ''} ${notification.error ? 'error' : ''}`}>
        {notification.message}
      </div>

      <div className="calibration-container">
        <section className="panel">
          <div className="panel-header">
            <h1>Extruder Throughput Calibration</h1>
          </div>
          <p>
            Build a throughput curve for one material and one nozzle. Weigh ten samples, calculate the four
            AiBuild coefficients, then copy them into a new nozzle in the components library.
          </p>
          <RelatedGuide
            title="Related guide"
            courseHref="/training/calibration"
            manualHref="/field-ops-manual.html?set=apollo&course=pe320-calibration"
          >
            Full procedure, robot timing, and AiBuild coefficient order live in the PE320 Throughput Calibration course and the Apollo cell manual.
          </RelatedGuide>

          <div className="material-info">
            <div className="input-row">
              <div className="input-field">
                <label htmlFor="material-name">Material Name:</label>
                <input
                  type="text"
                  id="material-name"
                  value={materialName}
                  onChange={(e) => { markDirty(); setMaterialName(e.target.value); }}
                  placeholder="e.g., PLA, ABS, PETG"
                />
              </div>
              <div className="input-field">
                <label htmlFor="nozzle-size">Nozzle Size (mm):</label>
                <select
                  id="nozzle-size"
                  value={nozzleSize}
                  onChange={(e) => { markDirty(); handleNozzleChange(e.target.value); }}
                >
                  <option value="">Select nozzle</option>
                  {NOZZLE_SIZES.map((size) => (
                    <option key={size} value={String(size)}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="input-field">
                <label htmlFor="min-rpm">Min Screw RPM:</label>
                <input
                  type="number"
                  id="min-rpm"
                  step="1"
                  min={SCREW_RPM_MIN}
                  max={nozzleMax ? nozzleMax - 1 : undefined}
                  value={minRpm}
                  onChange={(e) => { markDirty(); setMinRpm(e.target.value); }}
                  onBlur={handleMinRpmBlur}
                  disabled={!nozzleMax}
                />
              </div>
              <div className="input-field">
                <label htmlFor="max-rpm">Max Screw RPM:</label>
                <input
                  type="number"
                  id="max-rpm"
                  step="1"
                  min={SCREW_RPM_MIN + 1}
                  max={nozzleMax || undefined}
                  value={maxRpm}
                  onChange={(e) => { markDirty(); setMaxRpm(e.target.value); }}
                  onBlur={handleMaxRpmBlur}
                  disabled={!nozzleMax}
                />
              </div>
            </div>
            <button className="btn-primary" onClick={autoFillRpm}>
              Auto-fill RPM Values
            </button>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header clickable" onClick={() => setShowInstructions(!showInstructions)}>
            <h2>Instructions</h2>
            <span className="toggle-icon">{showInstructions ? '\u25B2' : '\u25BC'}</span>
          </div>

          <div className="instructions-brief">
            <p>
              <strong>Quick guide:</strong> Pick nozzle and material, auto-fill ten screw speeds, export
              Toolpath.zip, drop that zip into DirectoryLoader, run Toolpath on the robot, enter the
              weights, calculate, export CSV, then copy Linear (x), Quadratic (x2), Cubic (x3),
              Intercept (y) into AiBuild. Keep existing settings when you select the new nozzle.
            </p>
          </div>

          {showInstructions && (
            <div className="instructions-content">
              <h3>Why this matters</h3>
              <p>
                Doubling screw RPM does not double the material leaving the nozzle. The screw, the melt and
                the nozzle restriction make output bend away from a straight line. Each material also flows
                differently through each nozzle size. That is why throughput calibration is done as a pair:
                one material, one nozzle.
              </p>
              <p>
                We weigh the real output at ten screw speeds and fit a cubic curve. The four numbers from
                that curve go into the slicer (AiBuild). The slicer then works out what screw speed gives
                the flow it asked for. Skip this, or copy a number without its minus sign, and the print
                will be over- or under-extruded.
              </p>

              <h3>What you need</h3>
              <ul>
                <li>Purging bucket or tray</li>
                <li>Weighing scales</li>
                <li>Knife or scissors</li>
                <li>Dried pellets at the temperatures you will actually print</li>
              </ul>
              <p>
                The robot program times the 40 s discard and the 60 s catch. You do not need a stopwatch.
              </p>

              <h3>How to calibrate</h3>
              <ol>
                <li>
                  Select nozzle size and type the material name. Set the RPM range or click Auto-fill RPM
                  for ten evenly spaced speeds, fastest first. Limits: 5–80 rpm (3 mm), 5–120 rpm (5 mm),
                  5–160 rpm (8 / 10 / 12 mm).
                </li>
                <li>
                  Click Export Toolpath. The download is <strong>Toolpath.zip</strong> containing
                  {' '}<strong>Toolpath.src</strong> and <strong>Toolpath.dat</strong>. In Knowledge Centre
                  go to Program Files, open the KUKA Program Manager, and drop the zip into DirectoryLoader.
                  Do not drop a lone .src, and do not use a browser rename such as toolpath (1).src — KUKA
                  rejects that name.
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/kuka-program-manager.png"
                      alt="KUKA Program Manager upload drop zone"
                    />
                    <figcaption>Drop Toolpath.zip into DirectoryLoader in the KUKA Program Manager.</figcaption>
                  </figure>
                </li>
                <li>
                  On the pendant, load and run <strong>Toolpath</strong>. Extruder at print temperature.
                  Program override 100%. Extrusion multiplier 100%. The arm stays put. For sampling
                  technique, follow the{' '}
                  <a href="/training/calibration">PE320 Throughput Calibration training course</a>.
                </li>
                <li>
                  For each of the ten points: throw away the first 40 s. When the pendant says
                  CUT NOW — START catching, cut the stream and put the bucket under. 60 s later
                  CUT NOW — STOP catching: cut again, weigh only what was caught, enter it as g/min
                  against that screw RPM, then press START.
                </li>
                <li>
                  Click Calculate Coefficients, then Export CSV for a local copy.
                  <div className="instructions-callout">
                    <strong>These values live in this tab only</strong>
                    Closing or reloading this page will wipe the table and the coefficients unless you
                    have exported the CSV. The browser will warn you if you try to leave with unsaved work.
                  </div>
                </li>
                <li>
                  In AiBuild: Components Library, Nozzles, Add Component. Select the correct tool first —
                  it cannot be changed after the nozzle is created. Name the nozzle with manufacturer,
                  material, size and the temperatures you ran, for example
                  {' '}<strong>Mitsubishi PETG GF 30 5mm_80_125_180_235</strong>.
                  Copy the four coefficients in this order: Linear (x), Quadratic (x2), Cubic (x3),
                  Intercept (y). Use the Copy buttons so minus signs cannot be dropped.
                  <div className="instructions-callout critical">
                    <strong>Copy every minus sign</strong>
                    Missing a negative value puts the wrong curve into the slicer. The print will
                    extrude the wrong amount and cannot be fixed by tweaking speed later.
                  </div>
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/aibuild-components-library.png"
                      alt="AiBuild Components Library Nozzles tab"
                    />
                    <figcaption>Components Library, Nozzles, Add Component.</figcaption>
                  </figure>
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/aibuild-edit-nozzle-top.png"
                      alt="AiBuild Edit nozzle name, tool and diameter"
                    />
                    <figcaption>Tool is locked after create. Name includes material, size and temperatures.</figcaption>
                  </figure>
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/aibuild-edit-nozzle-coefficients.png"
                      alt="AiBuild throughput calibration coefficient fields"
                    />
                    <figcaption>Paste Linear (x), Quadratic (x2), Cubic (x3), Intercept (y) in this order.</figcaption>
                  </figure>
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/aibuild-nozzle-naming.png"
                      alt="Example nozzle name with material, size and temperatures"
                    />
                    <figcaption>Example name: manufacturer, material, nozzle size, zone temperatures.</figcaption>
                  </figure>
                </li>
                <li>
                  Reload the AiBuild page. Select the new nozzle in the workflow. Accept Compute all.
                  On Override settings choose <strong>Keep existing</strong>. Then compute, download the
                  new toolpath, upload it to the robot, and print.
                  <div className="instructions-callout critical">
                    <strong>Do not click Override</strong>
                    Override replaces the workflow settings with the nozzle defaults. That change cannot
                    be undone.
                  </div>
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/aibuild-workflow-nozzle-select.png"
                      alt="Select the new nozzle in the AiBuild workflow"
                    />
                    <figcaption>Reload, then select the new nozzle in the workflow.</figcaption>
                  </figure>
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/aibuild-compute-all.png"
                      alt="AiBuild Compute all dialog"
                    />
                    <figcaption>Selecting the nozzle requires Compute all.</figcaption>
                  </figure>
                  <figure className="instructions-figure">
                    <img
                      src="/images/training/pe320-calibration/aibuild-keep-existing.png"
                      alt="AiBuild Override settings dialog with Keep existing"
                    />
                    <figcaption>Choose Keep existing. Do not Override.</figcaption>
                  </figure>
                </li>
              </ol>
            </div>
          )}
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2>Calibration Data</h2>
            <div className="header-actions">
              <button className="btn-secondary" onClick={saveData}>Save Data</button>
              <button className="btn-secondary" onClick={loadData}>Load Data</button>
              <button className="btn-secondary" onClick={exportSrc}>Export Toolpath</button>
              <button className="btn-primary" onClick={calculateCoefficients}>Calculate Coefficients</button>
            </div>
          </div>

          <div className="table-actions">
            <button className="btn-secondary" onClick={() => addRow()}>Add Row</button>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Throughput (g/min)</th>
                <th>Throughput (kg/hr)</th>
                <th>Screw Speed (rpm)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{index + 1}</td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={row.gmin}
                      onChange={(e) => handleRowChange(row.id, 'gmin', e.target.value)}
                    />
                  </td>
                  <td className="kghr">{calculateKgHr(row.gmin)}</td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max={nozzleMax || undefined}
                      value={row.rpm}
                      onChange={(e) => handleRowChange(row.id, 'rpm', e.target.value)}
                      onBlur={(e) => handleRowRpmBlur(row.id, e.target.value)}
                    />
                  </td>
                  <td>
                    <button className="btn-delete" onClick={() => removeRow(row.id)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {coefficients && (
          <section className="panel">
            <div className="panel-header">
              <h2>Calibration Coefficients</h2>
              <div className="header-actions">
                <button className="btn-secondary" onClick={copyAllCoefficients}>Copy all</button>
                <button className="btn-secondary" onClick={exportCsv}>Export CSV</button>
              </div>
            </div>

            <p className="coef-order-note">
              Same labels and order as AiBuild: Linear (x), Quadratic (x2), Cubic (x3), Intercept (y).
              Copy every minus sign.
            </p>

            <table className="coefficients-table">
              <thead>
                <tr>
                  <th>Linear (x)</th>
                  <th>Quadratic (x2)</th>
                  <th>Cubic (x3)</th>
                  <th>Intercept (y)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="coef-cell">
                      <span>{coefficients.coef1}</span>
                      <button type="button" className="btn-copy" onClick={() => copyText(coefficients.coef1, 'Linear (x)')}>Copy</button>
                    </div>
                  </td>
                  <td>
                    <div className="coef-cell">
                      <span>{coefficients.coef2}</span>
                      <button type="button" className="btn-copy" onClick={() => copyText(coefficients.coef2, 'Quadratic (x2)')}>Copy</button>
                    </div>
                  </td>
                  <td>
                    <div className="coef-cell">
                      <span>{coefficients.coef3}</span>
                      <button type="button" className="btn-copy" onClick={() => copyText(coefficients.coef3, 'Cubic (x3)')}>Copy</button>
                    </div>
                  </td>
                  <td>
                    <div className="coef-cell">
                      <span>{coefficients.coef0}</span>
                      <button type="button" className="btn-copy" onClick={() => copyText(coefficients.coef0, 'Intercept (y)')}>Copy</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        )}

        {coefficients && (
          <section className="panel chart-panel">
            <div className="chart-wrapper">
              <canvas ref={chartRef}></canvas>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default CalibrationTool;
